// The registers (all stored in the shared `items` table) and how each one looks.
import type { FieldSpec } from '../ui/RecordDrawer';

export type ItemType = 'rfi' | 'notice' | 'variation' | 'claim' | 'nda';

export type Item = {
  id: string;
  site_id: string;
  type: ItemType;
  subtype: string | null;
  number: string | null;
  ref_no: string | null;
  title: string;
  description: string | null;
  status: string;
  building_ids: string[];
  buildings_raw: string[];
  org_id: string | null;
  org_raw: string | null;
  directed_to: string[];
  directed_raw: string[];
  submitted_on: string | null;
  respond_by: string | null;
  amount: number | null;
  amount_basis: 'VEP' | 'VIP' | null;
  notes: string | null;
  remark: string | null;
  response: string | null;
  data: Record<string, unknown>;
  stage: string | null;
  onus_org_id: string | null;
  onus_since: string | null;
  eot_days_claimed: number | null;
  eot_days_granted: number | null;
  review: ReviewFlag[];
  review_ok: string[];
  c3_edited_at: string | null;
  ss_row_id: number | null;
  ss_created_at: string | null;
  ss_created_by: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  deleted_at: string | null;
};

export const STATUS: Record<string, { label: string; tone: 'neutral' | 'orange' | 'dark' | 'good' | 'warn'; closed?: boolean }> = {
  open: { label: 'Open', tone: 'orange' },
  in_process: { label: 'In process', tone: 'orange' },
  hold: { label: 'Hold', tone: 'warn' },
  pending: { label: 'Pending', tone: 'orange' },
  approved: { label: 'Approved', tone: 'good' },
  rejected: { label: 'Rejected', tone: 'dark', closed: true },
  complete: { label: 'Complete', tone: 'good', closed: true },
  closed: { label: 'Closed', tone: 'neutral', closed: true },
  paid: { label: 'Paid', tone: 'good', closed: true },
  signed: { label: 'Signed', tone: 'good', closed: true },
  void: { label: 'Void', tone: 'neutral', closed: true },
};
/** Some registers name a status their own way (an RFI is "Answered", a notice "Resolved"). */
export const STATUS_BY_TYPE: Partial<Record<ItemType, Record<string, string>>> = {
  rfi: { pending: 'Awaiting response', complete: 'Answered' },
  notice: { open: 'Received', in_process: 'Being assessed', pending: 'Responded', approved: 'Determined', closed: 'Resolved' },
  variation: { open: 'Requested' },
  claim: { open: 'Submitted', in_process: 'In review', approved: 'Certified' },
};
export const statusLabel = (s: string, type?: ItemType) => (type && STATUS_BY_TYPE[type]?.[s]) || STATUS[s]?.label || s;
export const isClosed = (s: string) => Boolean(STATUS[s]?.closed);

export type RegisterDef = {
  module: string; // module key = URL segment
  type: ItemType;
  label: string;
  one: string;
  statuses: string[];
  subtypes?: string[];
  money?: 'VEP' | 'VIP';
  hasRespondBy: boolean;
  hasOrg: boolean;
  /** highlighted extra fields (kept in item.data) shown near the top of the item page */
  keyData?: string[];
};

export const REGISTERS: RegisterDef[] = [
  {
    module: 'rfis',
    type: 'rfi',
    label: 'RFIs & site instructions',
    one: 'RFI / SI',
    statuses: ['open', 'pending', 'complete', 'hold', 'closed', 'void'],
    subtypes: ['RFI', 'Architects SI', 'Engineers SI', 'Engineers Approval', 'Client Approval', "KA's Approval"],
    hasRespondBy: true,
    hasOrg: false,
    keyData: ['Project RFI #', 'Official RFI Doc sent', 'Official SI Doc Sent'],
  },
  {
    module: 'notices',
    type: 'notice',
    label: 'Notices (NOPD/GC/DT/EOT)',
    one: 'notice',
    statuses: ['open', 'in_process', 'pending', 'approved', 'hold', 'closed', 'void'],
    subtypes: ['NOPD Notice of Potential Delay', '(GC) General Communication', '(DT) Document Transfer', '(EOT) Extension of Time'],
    hasRespondBy: true,
    hasOrg: false,
    keyData: ['Project NOPD #', 'Document Date', 'Official Doc sent'],
  },
  {
    module: 'variations',
    type: 'variation',
    label: 'Variations & Requests',
    one: 'variation',
    statuses: ['open', 'in_process', 'hold', 'approved', 'rejected', 'complete', 'void'],
    subtypes: ['Var', 'Claim', 'Additional Project', 'Maintenance Costs', 'Other'],
    money: 'VEP',
    hasRespondBy: true,
    hasOrg: true,
    keyData: ['Ref/CTC #', 'Ready for Certificate', 'Ready to Proceed', 'Progress of works', 'TIME SENSETIVE'],
  },
  {
    module: 'claims',
    type: 'claim',
    label: 'Claims',
    one: 'claim',
    statuses: ['open', 'in_process', 'hold', 'approved', 'rejected', 'paid', 'void'],
    money: 'VIP',
    hasRespondBy: true,
    hasOrg: true,
    keyData: ['Follow-up Next Claim', 'CLIENT/REF #', 'PROJECT #', 'Claim Date', 'Ready for Certification', 'Docu-Sign Date', 'Finance Comments'],
  },
  {
    module: 'ndas',
    type: 'nda',
    label: 'NDAs',
    one: 'NDA',
    statuses: ['pending', 'signed', 'void'],
    hasRespondBy: false,
    hasOrg: true,
    keyData: ['Person Signed', 'Date Signed', 'Saved in Drive', 'Signed Attachment', 'Needs attention'],
  },
];

export const registerFor = (module: string | undefined) => REGISTERS.find((r) => r.module === module);
export const registerOfType = (t: string) => REGISTERS.find((r) => r.type === t);

/** Fields editable in the item drawer (main columns only; Smartsheet extras stay in "More details"). */
export function itemFields(
  def: RegisterDef,
  buildings: { id: string; name: string }[],
  orgs: { id: string; name: string }[],
): FieldSpec[] {
  const f: FieldSpec[] = [
    { key: 'title', label: def.type === 'nda' ? 'Contractor / individual' : 'Topic', type: 'textarea', required: true },
    { key: 'status', label: 'Status', type: 'select', required: true, options: def.statuses.map((s) => ({ value: s, label: statusLabel(s, def.type) })) },
  ];
  if (def.subtypes) f.push({ key: 'subtype', label: 'Type', type: 'select', options: def.subtypes.map((s) => ({ value: s, label: s })) });
  if (def.type !== 'nda') f.push({ key: 'ref_no', label: def.type === 'claim' ? 'Claim #' : def.type === 'variation' ? 'VAR ref #' : 'PBS CTC #' });
  f.push({ key: 'building_ids', label: 'Buildings', type: 'multi', options: buildings.map((b) => ({ value: b.id, label: b.name })) });
  if (def.hasOrg) f.push({ key: 'org_id', label: 'Contractor', type: 'select', options: orgs.map((o) => ({ value: o.id, label: o.name })) });
  if (def.type !== 'nda') f.push({ key: 'directed_to', label: 'Directed to', type: 'multi', options: orgs.map((o) => ({ value: o.id, label: o.name })) });
  if (def.money) f.push({ key: 'amount', label: `Amount (FJD ${def.money})`, type: 'number' });
  if (def.type !== 'nda') f.push({ key: 'submitted_on', label: 'Date submitted', type: 'date' });
  if (def.hasRespondBy) f.push({ key: 'respond_by', label: 'Respond by', type: 'date' });
  if (def.type === 'notice') {
    f.push({ key: 'eot_days_claimed', label: 'EOT days claimed', type: 'number' });
    f.push({ key: 'eot_days_granted', label: 'EOT days granted', type: 'number' });
  }
  f.push({ key: 'notes', label: 'Comments / notes', type: 'textarea', wide: true });
  if (def.type !== 'nda') {
    f.push({ key: 'remark', label: def.type === 'variation' ? 'Hold remarks' : 'Remark / directive', type: 'textarea' });
    if (def.type === 'rfi' || def.type === 'notice') f.push({ key: 'response', label: 'Response', type: 'textarea' });
  }
  return f;
}

export const showValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
};

// ---------- "to be reviewed" flags (worked out by the database on every save) ----------
export type ReviewFlag = { k: string; field: 'building' | 'directed' | 'org' | 'title' | 'submitted_on' | 'respond_by' | 'amount' | 'status' | string; raw: string | null };
export const REVIEW_FIELD: Record<string, string> = {
  building: 'Building',
  directed: 'Directed to',
  org: 'Contractor',
  title: 'Topic',
  submitted_on: 'Date submitted',
  respond_by: 'Respond by',
  amount: 'Amount',
  status: 'Approval value',
};
export const needsReview = (i: { review?: ReviewFlag[] | null }) => Boolean(i.review && i.review.length);

// ---------- type identity: every register has its own colour, icon and name ----------
export const TYPE_LOOK: Record<ItemType, { tag: string; name: string; color: string; tint: string; line: string; icon: string }> = {
  rfi: { tag: 'RFI', name: 'RFI / SI', color: '#1E5A8A', tint: '#EEF4F9', line: '#C9DCEB', icon: 'M4 5h16v11H9l-5 4z|M10 9.5a2 2 0 1 1 2.6 1.9c-.4.2-.6.5-.6.9v.4' },
  notice: { tag: 'NOTICE', name: 'Notice', color: '#6A3D8F', tint: '#F3EEF7', line: '#DCCDE8', icon: 'M5 21V4|M5 4h11l-2 4 2 4H5' },
  variation: { tag: 'VAR', name: 'Variation', color: '#0F6B6B', tint: '#EDF5F4', line: '#C6DFDC', icon: 'M7 4v16M3 8l4-4 4 4|M17 20V4M13 16l4 4 4-4' },
  claim: { tag: 'CLAIM', name: 'Claim', color: '#7A5200', tint: '#F7F1E6', line: '#E8D9BC', icon: 'M3 6h18v12H3z|M12 9.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z' },
  nda: { tag: 'NDA', name: 'NDA', color: '#4A4A4A', tint: '#F0F0F0', line: '#DADADA', icon: 'M5 10h14v10H5z|M8 10V7a4 4 0 0 1 8 0v3' },
};

// ---------- stages: where an item is at (each stage implies a status) ----------
export type Stage = { key: string; label: string; status: string; action?: string };
const RFI_STAGES: Stage[] = [
  { key: 'raised', label: 'Raised', status: 'open', action: 'Send to architects' },
  { key: 'sent', label: 'Sent to architects', status: 'open', action: 'Mark awaiting response' },
  { key: 'awaiting', label: 'Awaiting response', status: 'pending', action: 'Record answer' },
  { key: 'answered', label: 'Answered', status: 'complete', action: 'Close RFI' },
  { key: 'closed', label: 'Closed', status: 'closed' },
];
const NOTICE_STAGES: Stage[] = [
  { key: 'received', label: 'Received', status: 'open', action: 'Start assessment' },
  { key: 'assessed', label: 'Assessed', status: 'in_process', action: 'Record our response' },
  { key: 'responded', label: 'Responded', status: 'pending', action: 'Mark resolved' },
  { key: 'resolved', label: 'Resolved', status: 'closed' },
];
const EOT_STAGES: Stage[] = [
  { key: 'claimed', label: 'Claimed', status: 'open', action: 'Start review' },
  { key: 'review', label: 'Under review', status: 'in_process', action: 'Record determination' },
  { key: 'determined', label: 'Determined', status: 'approved', action: 'Close' },
  { key: 'closed', label: 'Closed', status: 'closed' },
];
const VAR_STAGES: Stage[] = [
  { key: 'requested', label: 'Requested', status: 'open', action: 'Mark priced' },
  { key: 'priced', label: 'Priced', status: 'in_process', action: 'Start SWI review' },
  { key: 'swi_review', label: 'SWI review', status: 'in_process', action: 'Send for client approval' },
  { key: 'client_approval', label: 'Client approval', status: 'in_process', action: 'Mark approved' },
  { key: 'in_progress', label: 'In progress', status: 'approved', action: 'Mark complete' },
  { key: 'complete', label: 'Complete', status: 'complete' },
];
const CLAIM_STAGES: Stage[] = [
  { key: 'submitted', label: 'Submitted', status: 'open', action: 'Start SWI review' },
  { key: 'swi_review', label: 'SWI review', status: 'in_process', action: 'Send to architect' },
  { key: 'architect_review', label: 'Architect review', status: 'in_process', action: 'Mark certified' },
  { key: 'certified', label: 'Certified', status: 'approved', action: 'Send to client finance' },
  { key: 'client_approval', label: 'Client approval', status: 'approved', action: 'Mark paid' },
  { key: 'paid', label: 'Paid', status: 'paid' },
];
const NDA_STAGES: Stage[] = [
  { key: 'drafted', label: 'Drafted', status: 'pending', action: 'Mark sent' },
  { key: 'sent', label: 'Sent', status: 'pending', action: 'Mark signed' },
  { key: 'signed', label: 'Signed', status: 'signed', action: 'Mark filed' },
  { key: 'filed', label: 'Filed', status: 'signed' },
];
export const isEot = (i: Pick<Item, 'type' | 'subtype'>) => i.type === 'notice' && /\bEOT\b/i.test(i.subtype ?? '');
export function stagesFor(i: Pick<Item, 'type' | 'subtype'>): Stage[] {
  switch (i.type) {
    case 'rfi': return RFI_STAGES;
    case 'notice': return isEot(i) ? EOT_STAGES : NOTICE_STAGES;
    case 'variation': return VAR_STAGES;
    case 'claim': return CLAIM_STAGES;
    default: return NDA_STAGES;
  }
}
/** The stage an item is at: the one saved, else the furthest stage that matches its status. */
export function stageIndex(i: Pick<Item, 'type' | 'subtype' | 'stage' | 'status'>): number {
  const st = stagesFor(i);
  const saved = st.findIndex((s) => s.key === i.stage);
  if (saved >= 0) return saved;
  for (let n = st.length - 1; n >= 0; n--) if (st[n].status === i.status) return n;
  return 0;
}
